"use client"
import { useTranslations } from "next-intl"
import { profileShareUrl } from "@/lib/profile-utils"

import { useState, useEffect, useRef } from "react"
import { Check, Flag, MoreVertical, Star, Upload } from "lucide-react"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { ReportDialog } from "@/components/ui/report-dialog"
import { ShareSheet } from "@/components/share/share-sheet"
import { ProfileReviewsDialog } from "@/components/reviews/profile-reviews-dialog"

interface ProfileActionsMenuProps {
  profileId: string
  username: string
  avatarUrl?: string | null
  bio?: string
  currentUserId?: string
  triggerClassName?: string
  iconClassName?: string
  showShareInMenu?: boolean
  showReviewsInMenu?: boolean
}

const getReportKey = (userId: string, profileId: string) => `report:${userId}:user:${profileId}`

export function ProfileActionsMenu({
  profileId,
  username,
  avatarUrl,
  bio,
  currentUserId,
  triggerClassName,
  iconClassName,
  showShareInMenu = true,
  showReviewsInMenu = false,
}: ProfileActionsMenuProps) {
  const t = useTranslations("profile");
  const tReport = useTranslations("report");
  const tReviews = useTranslations("reviews");
  const [open, setOpen] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [reviewsOpen, setReviewsOpen] = useState(false)
  const [reported, setReported] = useState(false)
  const shareRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (typeof window === "undefined" || !currentUserId) {
      setReported(false)
      return
    }
    try {
      setReported(window.localStorage.getItem(getReportKey(currentUserId, profileId)) === "1")
    } catch {
      setReported(false)
    }
  }, [currentUserId, profileId])

  const markIgnoreNextCardClick = () => {
    ;(globalThis as any).__kraahlIgnoreNextCardClickAt = Date.now()
  }

  const markReported = () => {
    setReported(true)
    if (!currentUserId) return
    try {
      window.localStorage.setItem(getReportKey(currentUserId, profileId), "1")
    } catch {}
  }

  const profileUrl = profileShareUrl(username)

  const handleShare = () => {
    markIgnoreNextCardClick()
    setOpen(false)
    setTimeout(() => {
      shareRef.current?.click()
    }, 50)
  }

  const isSelf = Boolean(currentUserId && currentUserId === profileId)
  const hasReviewsItem = showReviewsInMenu
  const hasShareItem = showShareInMenu
  const hasReportItem = !isSelf
  const hasAnyMenuItem = hasReviewsItem || hasShareItem || hasReportItem

  if (!hasAnyMenuItem) {
    return null
  }

  return (
    <>
      {hasShareItem ? (
        <ShareSheet
          url={profileUrl}
          title={t("shareProfileTitle", { username })}
          image={avatarUrl ?? undefined}
          username={username}
          bio={bio?.trim() || undefined}
          variant="profile"
          side="left"
          align="end"
        >
          <button ref={shareRef} type="button" className="sr-only" aria-hidden tabIndex={-1}>
            Share
          </button>
        </ShareSheet>
      ) : null}

      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={t("moreActions")}
            data-stop-prop
            className={triggerClassName}
            onPointerDown={(e) => {
              markIgnoreNextCardClick()
              e.stopPropagation()
            }}
            onMouseDown={(e) => {
              markIgnoreNextCardClick()
              e.stopPropagation()
            }}
          >
            <MoreVertical className={iconClassName} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {hasReviewsItem ? (
            <DropdownMenuItem
              onSelect={() => {
                markIgnoreNextCardClick()
                setOpen(false)
                setReviewsOpen(true)
              }}
            >
              <Star className="mr-2 h-4 w-4" />
              {tReviews("profileTitle")}
            </DropdownMenuItem>
          ) : null}
          {hasShareItem ? (
            <>
              {hasReviewsItem ? <DropdownMenuSeparator /> : null}
              <DropdownMenuItem onSelect={handleShare}>
                <Upload className="mr-2 h-4 w-4" />
                {t("shareProfile")}
              </DropdownMenuItem>
            </>
          ) : null}
          {hasReportItem ? (
            <>
              {hasReviewsItem || hasShareItem ? <DropdownMenuSeparator /> : null}
              <DropdownMenuItem
                disabled={reported}
                onSelect={(e) => {
                  e.preventDefault()
                  markIgnoreNextCardClick()
                  e.stopPropagation()
                  if (reported) return
                  setOpen(false)
                  requestAnimationFrame(() => {
                    setDialogOpen(true)
                  })
                }}
                className="text-red-500 focus:text-red-500"
              >
                {reported ? <Check className="mr-2 h-4 w-4" /> : <Flag className="mr-2 h-4 w-4" />}
                {reported ? tReport("reported") : tReport("report")}
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      {hasReviewsItem ? (
        <ProfileReviewsDialog
          open={reviewsOpen}
          onOpenChange={setReviewsOpen}
          profileId={profileId}
          showGiven={isSelf}
        />
      ) : null}

      <ReportDialog
        open={dialogOpen}
        onOpenChangeAction={(next) => {
          setDialogOpen(next)
          if (next) {
            setOpen(false)
          }
        }}
        itemType="user"
        itemId={profileId}
        itemTitle={username}
        currentUserId={currentUserId}
        reportedUserId={profileId}
        reportedUsername={username}
        reportedAvatarUrl={avatarUrl ?? null}
        alreadyReported={reported}
        onReported={markReported}
      />
    </>
  )
}
